import React, { useEffect, useState } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import Switch from "react-switch";
import { editMailingAddress } from "../utils/data";
import { BsChevronDown } from "react-icons/bs";
import { useUpdateUserProfile } from "../hooks/updateUserProfile";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

const EditMailingAddress = ({ userProfile, isLoading, refetch }) => {
  const {
    updateProfile,
    isLoading: isUpdating,
    error,
  } = useUpdateUserProfile();

  // Add loading state for component initialization
  const [isInitializing, setIsInitializing] = useState(true);

  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    type: "",
    streetAddress: "",
    suite: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    preferred: false,
  });
  const [customLabel, setCustomLabel] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [overflowStatus, setOverflowStatus] = useState(false);
  const [mailingAddressTypeIcon, setMailingAddressTypeIcon] = useState(false);

  const mailingAddressTypeIconRotate = mailingAddressTypeIcon
    ? "rotate(180deg)"
    : "rotate(0)";

  // Parse address data into components
  const parseAddress = (addressData) => {
    const defaultAddress = {
      streetAddress: "",
      suite: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    };

    if (!addressData || typeof addressData !== "object") {
      return defaultAddress;
    }

    // Handle mailingAddresses object structure
    return {
      streetAddress: addressData.streetAddress || "",
      suite: addressData.suite || "",
      city: addressData.city || "",
      state: addressData.province || addressData.state || "",
      country: addressData.country || "",
      postalCode: addressData.postalCode || "",
    };
  };

  // Initialize addresses from userProfile
  useEffect(() => {
    if (userProfile) {
      const addressList = [];

      // Handle different userProfile structures
      const profile = userProfile;

      // Handle mailingAddresses array structure
      if (profile.mailingAddresses && Array.isArray(profile.mailingAddresses)) {
        profile.mailingAddresses.forEach((addr) => {
          const addressParts = parseAddress(addr);
          if (addressParts.streetAddress || addressParts.city) {
            addressList.push({
              id: addr.id || `mailing-${addr.id}`,
              type: addr.addressType
                ? addr.addressType.charAt(0).toUpperCase() +
                  addr.addressType.slice(1).toLowerCase()
                : "Home",
              ...addressParts,
              preferred: addr.preferred, // First address is preferred by default
              originalId: addr.id, // Keep track of original ID for updates
            });
          }
        });
      }

      // Ensure at least one address exists if none were found
      if (addressList.length === 0) {
        addressList.push({
          id: "new-1",
          type: "Home",
          streetAddress: "",
          suite: "",
          city: "",
          state: "",
          country: "",
          postalCode: "",
          preferred: true,
        });
      }

      // Ensure exactly one address is marked as preferred
      const preferredCount = addressList.filter(
        (addr) => addr.preferred
      ).length;
      if (preferredCount === 0) {
        addressList[0].preferred = true;
      } else if (preferredCount > 1) {
        // Keep only the first preferred address
        let foundFirst = false;
        addressList.forEach((addr) => {
          if (addr.preferred && !foundFirst) {
            foundFirst = true;
          } else if (addr.preferred) {
            addr.preferred = false;
          }
        });
      }

      setAddresses(addressList);
      setIsInitializing(false);
    } else {
      setIsInitializing(false);
    }
  }, [userProfile]);

  const handleAddressChange = (id, field, value) => {
    setAddresses((prev) =>
      prev.map((address) =>
        address.id === id ? { ...address, [field]: value } : address
      )
    );
  };

  const handlePreferredChange = (id, preferred) => {
    setAddresses((prev) =>
      prev.map((address) => ({
        ...address,
        preferred: address.id === id ? preferred : false,
      }))
    );
  };

  const handleRemoveAddress = (id) => {
    setAddresses((prev) => prev.filter((address) => address.id !== id));
  };

  const validateAddress = (address) => {
    if (!address || typeof address !== "object") {
      return false;
    }

    // At minimum, require street address and either city or country
    const hasStreetAddress =
      address.streetAddress && address.streetAddress.trim() !== "";
    const hasCity = address.city && address.city.trim() !== "";
    const hasCountry = address.country && address.country.trim() !== "";

    return hasStreetAddress && (hasCity || hasCountry);
  };

  const handleAddAddress = () => {
    if (!newAddress.type) {
      toast.error("Please select an address type");
      return;
    }

    if (!validateAddress(newAddress)) {
      toast.error("Please fill in at least street address, city, and country");
      return;
    }

    const addressType =
      newAddress.type === "custom" ? customLabel : newAddress.type;

    const newAddressEntry = {
      id: `new-${Date.now()}`,
      type: addressType,
      streetAddress: newAddress.streetAddress,
      suite: newAddress.suite,
      city: newAddress.city,
      state: newAddress.state,
      country: newAddress.country,
      postalCode: newAddress.postalCode,
      preferred: newAddress.preferred,
    };

    // If this is set as preferred, unset others
    if (newAddress.preferred) {
      setAddresses((prev) => [
        ...prev.map((address) => ({ ...address, preferred: false })),
        newAddressEntry,
      ]);
    } else {
      setAddresses((prev) => [...prev, newAddressEntry]);
    }

    // Reset form
    setNewAddress({
      type: "",
      streetAddress: "",
      suite: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      preferred: false,
    });
    setCustomLabel("");
    setShowAddForm(false);
    setOverflowStatus(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (addresses.length === 0) {
      toast.error("At least one address is required");
      return;
    }

    // Validate all addresses
    const invalidAddresses = addresses.filter(
      (address) => !validateAddress(address)
    );
    if (invalidAddresses.length > 0) {
      toast.error(
        "Please ensure all addresses have at least street address, city, and country"
      );
      return;
    }

    // Find preferred address
    const preferredAddress = addresses.find((address) => address.preferred);

    try {
      // Create mailingAddresses array in the new format
      const mailingAddresses = addresses.map((address) => {
        const addressData = {
          streetAddress: address.streetAddress,
          city: address.city,
          province: address.state,
          postalCode: address.postalCode,
          country: address.country,
          addressType: address.type.toUpperCase(),
          preferred: address.preferred || false,
        };

        // Keep original ID if it exists, otherwise let backend assign
        if (address.originalId) {
          addressData.id = address.originalId;
        }

        return addressData;
      });

      // Only send mailing address-related fields
      const updatedData = {
        id: userProfile.id,
        mailingAddresses: mailingAddresses,
      };

      await updateProfile(updatedData);

      const isFirstLogin = window.location.pathname.includes("first_login_profile");
      if (!isFirstLogin) {
        toast.success("Mailing addresses updated successfully!");
      }

      // Close modal
      const modal = document.getElementById("editMailingAddress");
      if (modal) {
        window.$(modal).modal("hide");
        if (refetch) {
          setTimeout(() => {
            refetch();
          }, 100);
        }
      }
    } catch (error) {
      console.error("Failed to update mailing addresses:", error);

      // Provide more specific error messages
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update mailing addresses. Please try again.";

      toast.error(errorMessage);
    }
  };

  const handleAddressTypeSelect = (type) => {
    setNewAddress((prev) => ({ ...prev, type }));
  };

  return (
    <div
      className="modal fade"
      id="editMailingAddress"
      tabIndex="-1"
      aria-labelledby="editMailingAddress"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <form onSubmit={handleSubmit}>
          <div className="modal-content border-0 overlay-rounded">
            <div className="modal-header mt-14 border-0 pb-2 mb-1">
              <label className="fs-15" id="editMailingAddress">
                EDIT: Mailing Addresses
              </label>

              <FaTimes
                size={15}
                className="cursor-pointer text_primary"
                data-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body py-0">
              <div className="d-flex justify-content-end">
                <p className="text_primary small-text">
                  Select Preferred Mailing Address
                </p>
              </div>

              {/* Existing Addresses */}
              {addresses.map((address, index) => (
                <div
                  key={address.id}
                  className="d-flex justify-content-between p-0 mb-4"
                >
                  <div className="d-flex small-text">
                    <div className="form-group row mb-0">
                      <label className="col-md-5 col-form-label pr-0">
                        {addresses.length > 1 && (
                          <MdClose
                            className="text_danger cursor-pointer mr-4"
                            size={20}
                            onClick={() => handleRemoveAddress(address.id)}
                          />
                        )}
                        {address.type} Address:
                      </label>
                      <div className="col-md-7 d-flex flex-column pl-0">
                        <input
                          type="text"
                          className="small-text overlay-phone-input mb-2"
                          placeholder="Street Address"
                          value={address.streetAddress}
                          onChange={(e) =>
                            handleAddressChange(
                              address.id,
                              "streetAddress",
                              e.target.value
                            )
                          }
                          disabled={isUpdating}
                          required
                        />
                        <input
                          type="text"
                          className="small-text overlay-phone-input mb-2"
                          placeholder="Suite/Apt No."
                          value={address.suite}
                          onChange={(e) =>
                            handleAddressChange(
                              address.id,
                              "suite",
                              e.target.value
                            )
                          }
                          disabled={isUpdating}
                        />
                        <div className="d-flex">
                          <div className="w-160 mr-3">
                            <input
                              type="text"
                              className="small-text overlay-phone-input mr-4 mb-2"
                              placeholder="City"
                              value={address.city}
                              onChange={(e) =>
                                handleAddressChange(
                                  address.id,
                                  "city",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                              required
                            />
                            <input
                              type="text"
                              className="small-text overlay-phone-input mr-4"
                              placeholder="Country / Region"
                              value={address.country}
                              onChange={(e) =>
                                handleAddressChange(
                                  address.id,
                                  "country",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                              required
                            />
                          </div>
                          <div className="w-160">
                            <input
                              type="text"
                              className="small-text overlay-phone-input mb-2"
                              placeholder="Province / State"
                              value={address.state}
                              onChange={(e) =>
                                handleAddressChange(
                                  address.id,
                                  "state",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                            />
                            <input
                              type="text"
                              className="small-text overlay-phone-input"
                              placeholder="Postal Code / Zip"
                              value={address.postalCode}
                              onChange={(e) =>
                                handleAddressChange(
                                  address.id,
                                  "postalCode",
                                  e.target.value
                                )
                              }
                              disabled={isUpdating}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex small-text">
                    {address.preferred && (
                      <div className="badge_pomme bg_primary f-10 text-light rounded-pill d-flex align-items-center justify-content-center">
                        <span>Preferred Mailing Address</span>
                      </div>
                    )}
                    <div className="ml-4">
                      <Switch
                        onChange={(checked) =>
                          handlePreferredChange(address.id, checked)
                        }
                        checked={address.preferred}
                        onColor="#5D8FA8"
                        onHandleColor="#50a193"
                        handleDiameter={20}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={14}
                        width={35}
                        disabled={isUpdating}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Another Address Button */}
              <div className={`mt-4 ${showAddForm ? "d-none" : ""}`}>
                <button
                  type="button"
                  className="btn btn-link text_primary f-11 text-decoration-none"
                  onClick={() => {
                    setShowAddForm(true);
                    setTimeout(() => {
                      setOverflowStatus(true);
                    }, 400);
                  }}
                >
                  <FaPlus size={10} className="mr-1" /> Add another Mailing
                  Address
                </button>
              </div>

              {/* Add New Address Form */}
              <div
                className={`d-flex justify-content-between p-0 slide-down ${
                  showAddForm ? "showFormMailing" : ""
                } ${overflowStatus ? "overflow-visible" : ""}`}
              >
                <div className="d-flex small-text">
                  <div
                    className="d-flex align-items-center"
                    style={{ height: 35 }}
                  >
                    <MdClose
                      className="text_danger cursor-pointer"
                      size={20}
                      onClick={() => {
                        setShowAddForm(false);
                        setOverflowStatus(false);
                        setNewAddress({
                          type: "",
                          streetAddress: "",
                          suite: "",
                          city: "",
                          state: "",
                          country: "",
                          postalCode: "",
                          preferred: false,
                        });
                        setCustomLabel("");
                      }}
                    />
                  </div>
                  <div className="form-group row mb-0 ml-2">
                    <div className="col-md-6">
                      <div className="dropdown">
                        <button
                          className="btn btn-block border text-left overlay-dropdown-input-small"
                          type="button"
                          id="dropdownMenuButton"
                          data-toggle="dropdown"
                          data-display="static"
                          aria-haspopup="true"
                          aria-expanded="false"
                          onClickCapture={() =>
                            setMailingAddressTypeIcon(!mailingAddressTypeIcon)
                          }
                        >
                          <div className="d-flex justify-content-between">
                            <small className="small-text">
                              {newAddress.type ||
                                "Select type of Mailing Address"}
                            </small>
                            <BsChevronDown
                              style={{
                                transform: mailingAddressTypeIconRotate,
                                transition: "all 0.3s linear",
                              }}
                            />
                          </div>
                        </button>
                        <div
                          className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                          aria-labelledby="dropdownMenuButton"
                        >
                          <p className="pl-4 mb-0 pb-2">
                            <small>Choose one type only:</small>
                          </p>
                          {editMailingAddress.map((item, key) => (
                            <a
                              key={key}
                              onClick={() =>
                                handleAddressTypeSelect(item.value)
                              }
                              className="dropdown-item activeHover ml-4 wa"
                              href="#"
                            >
                              <small>{item.label}</small>
                            </a>
                          ))}
                          <div className="ml-5 small-text mb-2">
                            <label className="pt-2">Custom label:</label>
                            <div className="pt-1 d-flex">
                              <input
                                type="text"
                                className="small-text overlay-input"
                                placeholder="Insert custom label"
                                value={customLabel}
                                onChange={(e) => setCustomLabel(e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-primary w-75 h-35 small-text ml-3"
                                onClick={() => {
                                  if (customLabel.trim()) {
                                    setNewAddress((prev) => ({
                                      ...prev,
                                      type: "custom",
                                    }));
                                  }
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center mt-3">
                        <Switch
                          onChange={(checked) =>
                            setNewAddress((prev) => ({
                              ...prev,
                              preferred: checked,
                            }))
                          }
                          checked={newAddress.preferred}
                          onColor="#5D8FA8"
                          onHandleColor="#50a193"
                          handleDiameter={16}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                          height={12}
                          width={30}
                        />
                        <small className="ml-2">Set as Preferred</small>
                      </div>
                    </div>

                    <div className="col-md-6 d-flex flex-column pl-0">
                      <input
                        type="text"
                        className="small-text overlay-phone-input mb-2"
                        placeholder="Street Address"
                        value={newAddress.streetAddress}
                        onChange={(e) =>
                          setNewAddress((prev) => ({
                            ...prev,
                            streetAddress: e.target.value,
                          }))
                        }
                      />
                      <input
                        type="text"
                        className="small-text overlay-phone-input mb-2"
                        placeholder="Suite/Apt No."
                        value={newAddress.suite}
                        onChange={(e) =>
                          setNewAddress((prev) => ({
                            ...prev,
                            suite: e.target.value,
                          }))
                        }
                      />
                      <div className="d-flex">
                        <div className="w-160 mr-3">
                          <input
                            type="text"
                            className="small-text overlay-phone-input mr-4 mb-2"
                            placeholder="City"
                            value={newAddress.city}
                            onChange={(e) =>
                              setNewAddress((prev) => ({
                                ...prev,
                                city: e.target.value,
                              }))
                            }
                          />
                          <input
                            type="text"
                            className="small-text overlay-phone-input mr-4"
                            placeholder="Country / Region"
                            value={newAddress.country}
                            onChange={(e) =>
                              setNewAddress((prev) => ({
                                ...prev,
                                country: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="w-160">
                          <input
                            type="text"
                            className="small-text overlay-phone-input mb-2"
                            placeholder="Province / State"
                            value={newAddress.state}
                            onChange={(e) =>
                              setNewAddress((prev) => ({
                                ...prev,
                                state: e.target.value,
                              }))
                            }
                          />
                          <input
                            type="text"
                            className="small-text overlay-phone-input"
                            placeholder="Postal Code / Zip"
                            value={newAddress.postalCode}
                            onChange={(e) =>
                              setNewAddress((prev) => ({
                                ...prev,
                                postalCode: e.target.value,
                              }))
                            }
                          />
                          <div className="d-flex mt-3 justify-content-end">
                            <button
                              type="button"
                              className="btn btn-outline-primary w-75 h-35 small-text"
                              onClick={handleAddAddress}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between mt-5">
              <button
                type="button"
                className="btn btn_outline_danger rounded overlay-btn"
                data-dismiss="modal"
              >
                Close
              </button>
              <button
                type="submit"
                className="btn btn-outline-primary rounded overlay-btn"
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMailingAddress;
